import {
  CampaignDto,
  CampaignListItemDto,
  apiService,
} from '@shared/services/apiService/apiService';
import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

const WS_URL = import.meta.env.VITE_API_URL.startsWith('/') ? '' : import.meta.env.VITE_API_URL;
const WS_PATH = import.meta.env.VITE_API_URL.startsWith('/')
  ? import.meta.env.VITE_API_URL
  : new URL(import.meta.env.VITE_API_URL).pathname;

interface WsException {
  status: string;
  message: string;
}

type CampaignSocketRow = CampaignListItemDto & Partial<CampaignDto>;

interface UseWebSocketGetCampaignsOptions {
  limit?: number;
  sortBy?: 'title' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
  withTotal?: boolean;
  companyId?: string;
  search?: string;
}

export const useWebSocketGetCampaigns = (
  onError?: <T>(error: T) => void,
  options: UseWebSocketGetCampaignsOptions = {},
) => {
  const {
    limit = 20,
    sortBy = 'createdAt',
    sortOrder = 'DESC',
    withTotal = false,
    companyId,
    search,
  } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [campaigns, setCampaigns] = useState<CampaignSocketRow[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalItems, setTotalItems] = useState<number | undefined>(undefined);
  const lastRequestedCursorRef = useRef<string | null>(null);

  const subscribeForCampaignsList = useCallback(
    (companyId: string) => {
      if (!companyId) return;
      socket?.emit('subscribe_to_get_campaigns_list', {
        companyId,
      });
    },
    [socket],
  );

  const [fetchCampaigns, { isFetching, isLoading, error }] =
    apiService.endpoints.campaignListPaginatedControllerGetCampaignListPaginated.useLazyQuery();

  const loadPage = useCallback(
    async (cursor?: string | null, shouldAppend = false) => {
      const normalizedCursor = cursor || null;
      if (shouldAppend && normalizedCursor && lastRequestedCursorRef.current === normalizedCursor) {
        return;
      }
      if (shouldAppend && normalizedCursor) {
        lastRequestedCursorRef.current = normalizedCursor;
      }

      const response = await fetchCampaigns({
        limit,
        cursor: cursor || undefined,
        sortBy,
        sortOrder,
        ...(withTotal ? { withTotal } : {}),
        ...(search ? { search } : {}),
      }).unwrap();

      setCampaigns((prev) => {
        const merged = shouldAppend ? [...prev, ...response.data] : response.data;
        const uniqueById = new Map<string, CampaignSocketRow>();
        merged.forEach((item) => uniqueById.set(item.id, item));
        return Array.from(uniqueById.values());
      });
      setNextCursor(response.meta.cursor);
      setHasMore(response.meta.hasMore);
      setTotalItems(response.meta.totalItems);
    },
    [fetchCampaigns, limit, search, sortBy, sortOrder, withTotal],
  );

  const refetchCampaignsList = useCallback(async () => {
    lastRequestedCursorRef.current = null;
    setNextCursor(null);
    setHasMore(false);
    await loadPage(null, false);
  }, [loadPage]);

  const loadMoreCampaigns = useCallback(async () => {
    if (!hasMore || !nextCursor || isFetching) return;
    await loadPage(nextCursor, true);
  }, [hasMore, isFetching, loadPage, nextCursor]);

  useEffect(() => {
    void refetchCampaignsList();
  }, [refetchCampaignsList]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken')!;
    const newSocket = io(`${WS_URL}/campaigns`, {
      path: `${WS_PATH}/socket.io`,
      transports: ['websocket'],
      autoConnect: true,
      auth: { token },
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
      setSocket(null);
    };
  }, []);

  useEffect(() => {
    subscribeForCampaignsList(companyId || campaigns[0]?.companyId || '');
  }, [companyId, campaigns, subscribeForCampaignsList]);

  useEffect(() => {
    if (!socket) return;

    const onConnect = () => {
      setIsConnected(true);
    };
    const onDisconnect = () => setIsConnected(false);

    const onGetCampaignsList = (value: { result: CampaignDto }) => {
      setCampaigns((prev) => {
        const index = prev.findIndex((campaign) => campaign.id === value.result.id);
        if (index === -1) return prev;
        const updatedList = [...prev];
        const existing = updatedList[index];
        updatedList[index] = {
          ...existing,
          ...value.result,
          agentsCount: value.result.agentsCount || existing.agentsCount,
        };
        return updatedList;
      });
    };

    const onException = (error: WsException) => {
      socket.disconnect();
      console.error(error);
      onError?.(error);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('get_campaigns_list', onGetCampaignsList);
    socket.on('exception', onException);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('get_campaigns_list', onGetCampaignsList);
      socket.off('exception', onException);
    };
  }, [socket]);

  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  return {
    isConnected,
    isLoading,
    isFetching,
    data: campaigns,
    hasMore,
    totalItems,
    loadMore: loadMoreCampaigns,
    refetch: refetchCampaignsList,
  };
};
